import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsPage = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const ownerName = settings?.owner_name || 'Muller Stéphane';
  const companyName = settings?.company_legal_name || 'Muller Construction SRL';
  const companyVat = settings?.company_vat || 'BE 0803.324.504';
  const companyAddress = settings?.company_address || 'Belgique';
  const companyCountry = settings?.company_country || 'Belgique';
  const companyPostalCode = settings?.company_postal_code || '';
  const companyEmail = settings?.company_email || 'contact@buildconnect.be';
  const platformName = settings?.platform_name || 'BuildConnect';
  const jurisdiction = settings?.legal_jurisdiction || "Tribunaux de l'arrondissement de l'exploitant";
  const customContent = settings?.terms_content;

  const fullAddress = [companyAddress, companyPostalCode, companyCountry].filter(Boolean).join(', ');

  if (customContent) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="terms-page">
        <SEOHead
          title="Conditions Générales d'Utilisation"
          description="Conditions générales d'utilisation de la plateforme BuildConnect. Marketplace professionnelle du bâtiment en Belgique, Luxembourg et France."
          path="/terms"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>
          <h1 className="font-heading font-bold text-3xl text-[#111827] mb-8">Conditions Générales d'Utilisation</h1>
          <div className="prose prose-lg max-w-none text-[#374151] whitespace-pre-wrap" data-testid="terms-custom-content">
            {customContent}
          </div>
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-[#6B7280]">
              <strong>Éditeur :</strong> {ownerName} | <strong>Exploitant :</strong> {companyName}<br />
              <strong>Siège social :</strong> {fullAddress}<br />
              <strong>TVA :</strong> {companyVat} | <strong>Contact :</strong> {companyEmail}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>
        
        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-8">Conditions Générales d'Utilisation</h1>
        <p className="text-sm text-[#6B7280] mb-6">Dernière mise à jour : Avril 2026</p>
        
        <div className="prose prose-lg max-w-none text-[#374151] space-y-6">
          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 1 - Définitions</h2>
            <p><strong>{platformName}</strong> (accessible sur buildconnect.be) est la propriété de <strong>{ownerName}</strong> et est exploité par <strong>{companyName}</strong>, société de droit belge, inscrite à la Banque-Carrefour des Entreprises sous le numéro {companyVat}.</p>
            <p>L'exploitant de la plateforme peut être modifié à tout moment par décision du propriétaire, sans que cela n'affecte la validité des présentes conditions.</p>
            <p><strong>Utilisateur</strong> désigne toute personne morale inscrite sur la plateforme, qu'elle soit Donneur d'ordre ou Sous-traitant.</p>
            <p><strong>Donneur d'ordre</strong> désigne une entreprise qui publie des chantiers et recherche des sous-traitants.</p>
            <p><strong>Sous-traitant</strong> désigne une entreprise qui postule aux chantiers publiés sur la plateforme.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 2 - Objet</h2>
            <p>Les présentes conditions générales régissent l'accès et l'utilisation de la plateforme {platformName}. En accédant à la plateforme, l'Utilisateur accepte sans réserve les présentes conditions.</p>
            <p>{platformName} est une marketplace de mise en relation entre entreprises de construction. {platformName} n'est pas partie aux contrats conclus entre les Utilisateurs.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 3 - Inscription et Compte</h2>
            <p>L'inscription est réservée aux personnes morales (entreprises) disposant d'un numéro de TVA valide en Belgique, au Luxembourg ou en France.</p>
            <p>L'Utilisateur s'engage à fournir des informations exactes, complètes et à jour. Toute usurpation d'identité ou fourniture de fausses informations entraînera la suspension immédiate du compte.</p>
            <p>L'Utilisateur est responsable de la confidentialité de ses identifiants de connexion.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 4 - Services et Abonnements</h2>
            <p><strong>Plan Gratuit :</strong> 1 publication de chantier, 1 candidature, profil basique, accès à la carte interactive.</p>
            <p><strong>Plan Premium :</strong> Publications et candidatures illimitées, messagerie directe, badges de vérification, analytics avancés.</p>
            <p>Les tarifs sont affichés hors TVA. La TVA belge de 21% est applicable conformément à la législation en vigueur.</p>
            <p>Le paiement s'effectue exclusivement par virement SEPA. L'abonnement est activé après réception du paiement.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 5 - Paiements et Escrow</h2>
            <p>{platformName} propose un service d'escrow (séquestre) pour sécuriser les paiements entre Donneurs d'ordre et Sous-traitants.</p>
            <p>Les fonds sont conservés sur un compte tiers jusqu'à la validation des jalons du projet par le Donneur d'ordre.</p>
            <p>{platformName} ne prélève aucune commission sur les transactions escrow pendant la période de lancement.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 6 - Responsabilités</h2>
            <p>{companyName}, en tant qu'exploitant de {platformName}, agit en tant qu'intermédiaire technique. La plateforme ne garantit pas :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>La qualité des prestations des sous-traitants</li>
              <li>La solvabilité des donneurs d'ordre</li>
              <li>L'exactitude des informations fournies par les utilisateurs</li>
            </ul>
            <p>Chaque Utilisateur est seul responsable de ses obligations légales (assurances, agréations, TVA, droit social).</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 7 - Propriété Intellectuelle</h2>
            <p>Le site buildconnect.be et l'ensemble de ses éléments (textes, graphismes, logiciels, logos) sont la propriété de {ownerName}. L'exploitation est confiée à {companyName}. Toute reproduction sans autorisation est interdite.</p>
            <p>Ce site a été créé via la plateforme <a href="https://www.emergent.sh" className="text-[#1E3A8A] underline" target="_blank" rel="noreferrer">Emergent</a>.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 8 - Résiliation</h2>
            <p>L'Utilisateur peut supprimer son compte à tout moment depuis la page Profil. La suppression entraîne l'effacement définitif de toutes les données.</p>
            <p>{companyName} se réserve le droit de suspendre ou supprimer tout compte en cas de violation des présentes conditions.</p>
            <p>Les abonnements en cours ne sont pas remboursables en cas de résiliation anticipée.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 9 - Droit Applicable et Litiges</h2>
            <p>Les présentes conditions sont régies par le <strong>droit belge</strong>.</p>
            <p>En cas de litige, les parties s'engagent à rechercher une solution amiable. À défaut, les {jurisdiction} seront seuls compétents.</p>
            <p>Conformément au Règlement européen n° 524/2013, le consommateur peut recourir à la plateforme de règlement en ligne des litiges : <a href="https://ec.europa.eu/consumers/odr" className="text-[#1E3A8A] underline" target="_blank" rel="noreferrer">https://ec.europa.eu/consumers/odr</a></p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">Article 10 - Sous-traitance et Obligations Légales Belges</h2>
            <p>Conformément à la <strong>loi du 12 avril 1965</strong> concernant la protection de la rémunération des travailleurs et aux dispositions du Code pénal social :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Le Donneur d'ordre est tenu de vérifier que le Sous-traitant est en règle avec ses obligations sociales et fiscales</li>
              <li>Le Sous-traitant doit disposer des agréations nécessaires pour les travaux concernés</li>
              <li>La responsabilité solidaire en matière de dettes sociales et fiscales s'applique conformément à l'article 30bis de la loi du 27 juin 1969</li>
            </ul>
            <p>{platformName} rappelle l'obligation de déclaration Limosa pour tout travailleur détaché et l'obligation de vérification via le portail de la sécurité sociale belge.</p>
          </section>

          <section className="border-t pt-6 mt-8">
            <p className="text-sm text-[#6B7280]">
              <strong>Éditeur :</strong> {ownerName} | <strong>Exploitant :</strong> {companyName}<br />
              <strong>Siège social :</strong> {fullAddress}<br />
              <strong>TVA :</strong> {companyVat} | <strong>Contact :</strong> {companyEmail}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
